import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useStableListParams } from '../../../../shared/hooks/useStableListParams';
import { contactApi } from '../services/contactsApi';
import type { ContactBulkScope } from '../services/contactsApi';
import type { ContactCreate, ContactUpdate, ListContactsParams } from '../types';

// Defined in the leaf `queryKeys` module and re-exported here so existing call
// sites are unchanged. New consumers should import from `./queryKeys` directly:
// this module is partially mocked in many specs, and a partial mock replaces the
// whole module — see that file's header.
export { contactKeys } from './queryKeys';
import { contactKeys } from './queryKeys';

export function useContacts(params?: ListContactsParams, options?: { enabled?: boolean }) {
  // Every field participates, by construction — see `useStableListParams`. The
  // hand-listed dep array this replaces had omitted `staleness`, so the whole
  // Staleness select was inert: the URL and the chip changed and no request ever
  // went out (WI 7800e951).
  const stableParams = useStableListParams(params);

  return useQuery({
    queryKey: contactKeys.list(stableParams),
    queryFn: () => contactApi.list(stableParams),
    enabled: options?.enabled ?? true,
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => contactApi.get(id),
    enabled: !!id,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ContactCreate) => contactApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ContactUpdate }) => contactApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

/**
 * Deletion-impact preview for the delete dialog — what would be orphaned, and
 * what still blocks.
 *
 * `enabled` is the dialog's own open state: the preview is a second round trip
 * and must not be paid for on every row render. `staleTime: 0` because the
 * answer is about OTHER modules' live records, which move independently of
 * anything this page has cached — a stale "safe to delete" is the one answer
 * this dialog must never show.
 */
export function useContactDeletionImpact(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...contactKeys.detail(id ?? ''), 'deletion-impact'],
    queryFn: () => contactApi.deletionImpact(id as string),
    enabled: enabled && !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useArchiveContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactApi.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useUnarchiveContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactApi.unarchive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useBulkDeleteContacts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: ContactBulkScope) => contactApi.bulkDelete(scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useBulkArchiveContacts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: ContactBulkScope) => contactApi.bulkArchive(scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}
